import InitialNode from './CustomNodes/InitialNode.jsx';
import Connector from './CustomNodes/Connector.jsx';
import IntermediateNode from './CustomNodes/IntermediateNode.jsx';
import OutputNode from './CustomNodes/OutputNode.jsx';
import { v4 as uuidv4 } from "uuid";
import isEqual from 'lodash.isequal';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ReactFlow, ReactFlowProvider, addEdge, Controls, useNodesState, useEdgesState, getIncomers, getOutgoers, getConnectedEdges } from '@xyflow/react';
import ButtonEdge from './CustomNodes/ButtonEdge.jsx';

import '@xyflow/react/dist/style.css';
import './canvas.css';
import { useDispatch, useSelector } from 'react-redux';
import canvasBackground from '../../assets/canvas_background.svg';
import { resetNodeType, setSelectedStep, setSelectedConnector, setWorkflow, setConnectorMenu, setMergeTargetNode, setWorkflowForms, setDeletedNodes, setChangeDetected, setOriginalWorkflow, setAddNodeInBetween, setInsertNodeBetween, setSidebarOpen } from '../../redux/slices/connectorSlice.js';
import StepDetailsForm from './StepDetailsForm.jsx';
import { createEdge, createNode, createJoinNode, getNodeEdgeToBeDeleted, getNodeXPosition, getNodeYPosition, sortNodesIndexWise, NODE_SPACING, CONNECTOR_SPACING, SHIFT_STEP, SHIFT_CIRCLE, BRANCH_Y_GAP } from '../../utils/createEdgeNode.js';
import ConditionManager from './components/ConditionManager.jsx';
import JoinRuleForm from './components/JoinRuleForm.jsx';
import WorkflowSidebar from './components/WorkflowSidebar.jsx';
import NotificationInfo from './components/NotificationInfo.jsx';
import HttpRequestPanel from './components/HttpRequestPanel.jsx';
import VerificationPanel from './components/VerificationPanel.jsx';
import OutlookIntegrationPanel from './components/OutlookIntegrationPanel.jsx';
import { MailOutlined, HomeOutlined } from '@ant-design/icons';
import { Flex, Button, Layout, message } from "antd"
import { useParams } from 'react-router-dom';
import { useTenantNavigation } from '../../hooks/useTenantNavigation.js';
import { deleteForm, deleteFormCondition, deleteWorkflowCondition, getFormCondition, getSpaceWorkflow, getWorkflowConditions, saveFormCOndition, saveNodeFormData, saveWorkflow, saveWorkflowCondition, upsertWorkflowAI, upsertWorkflowConditionAI } from '../../services/WorkflowServices.js';
import { getRoles } from '../../services/adminServices.js';
import { getAllWidgets } from '../../utils/formsHelper.js';
import { showLoader } from '../../utils/showloader.js';
import { getSpaceAllFormsByWorkflowId } from '../../services/requestServices.js';
import { fetchSpaceSpecificGroups, fetchSpaceSpecificUserPickers, fetchSpaceSpecificUsers } from '../../redux/actions/userRoleGroupAction.js';
import FigmaEdge from './CustomNodes/FigmaEdge.jsx';
import { checkNodeCondition, findParallelGroupId, findBranchInfo, findJoinNodeForGroup, getParallelRegionToDelete, relayoutParallelBranches } from '../../utils/workflowHelper.js';
import BreadCrumb from '../BreadCrumb.js';
import { getCurrentSpaceData } from '../../utils/currentUserDetail.js';
import GooglePlacesPanel from './components/GooglePlacesPanel.jsx';
import AssistantBot from '../ai-assistant/AssistantBot.jsx';
import { ASSISTANT_API_URL, SCOPE, listConditionsMessage, listFormsMessage } from '../ai-assistant/constants.js';
import '../ai-assistant/assistant-bot.css';
import { useTranslation } from 'react-i18next';
const { Header } = Layout;

const NODE_TYPE_LABEL = {
    step: 'Workflow Step',
    approvalStep: 'Approval Step',
    end: 'End',
    notification: 'Notification',
    httpRequest: 'HTTP Request',
    sharePublicForm: 'Share Public Form',
    duplicateCheck: 'Duplicate Check',
    outlookCalendar: 'Outlook Calendar',
    googlePlaces: 'Google Places',
};

const nodeTypes = {
    circle: Connector,
    textInput: IntermediateNode,
    default: InitialNode,
    output: OutputNode
};

const edgeTypes = {
    buttonedge: ButtonEdge,
    custom: FigmaEdge,
    figma: FigmaEdge
};

const VERIFICATION_TYPES = new Set([]);
const getNodeType = (node) => node?.data?.type || node?.type;
const getVerificationLabel = (node) => {
    const type = getNodeType(node);
    if (node?.data?.name) return node.data.name;
    return "Verification";
};

const defaultNodes = [
    {
        id: uuidv4(),
        data: { name: 'Intake' },
        position: {
            x: 100,
            y: 100,
        },
        type: 'default',
        draggable: true
    },
    {
        id: uuidv4(),
        data: {},
        position: {
            x: 430,
            y: 100,
        },
        type: 'circle',
        draggable: true
    }
]

const initialNodes = [
    {
        id: uuidv4(),
        data: { name: 'Start', stepColor: "#EB2F96", stepIcon: "fileEdit", type: 'start' },
        position: {
            x: 100,
            y: 100,
        },
        type: 'default',
        draggable: true
    }
];

const initialEdges = [
    {
        type: 'figma',
        source: defaultNodes[0]?.id,
        target: defaultNodes[1]?.id,
        id: uuidv4()
    }
];

const Canvas = () => {
    const dispatch = useDispatch();
    const navigate = useTenantNavigation();
    const { t } = useTranslation();
    const [workflowData, setWorkflowData] = useState({})
    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
    // Custom nodes change handler to ensure proper individual dragging
    const handleNodesChange = useCallback((changes) => {
        // Filter out unwanted bulk selections or position changes
        const filteredChanges = changes.filter(change => {
            // Allow position changes for individual nodes being dragged
            if (change.type === 'position') return true;
            // Allow other necessary changes but be selective
            if (change.type === 'select') return true;
            if (change.type === 'add' || change.type === 'remove') return true;
            return true; // Default to allowing the change
        });
        onNodesChange(filteredChanges);
    }, [onNodesChange]);
    const [deletedFlowNodes, setDeletedFlowNodes] = useState([])
    const [deletedEdges, setDeletedEdges] = useState([])
    const [workflowConditions, setWorkflowConditions] = useState([])
    const [breadcrumbs, setBreadCrumbs] = useState([])

    // Keep latest nodes/edges in refs to avoid stale values inside effects
    const nodesRef = useRef([])
    const edgesRef = useRef([])
    const flowWrapperRef = useRef(null)
    // Per-formId cache of fetched form conditions. Invalidated whenever the
    // assistant returns a formCondition mutation. See applyFormConditionFromAi.
    const formConditionsCacheRef = useRef({})
    // Synchronous mirror of workflowData so downstream appliers in the same
    // /ai response batch can read the freshly-saved workflowId without waiting
    // for React state to flush.
    const workflowDataRef = useRef({})

    const { nodeType, selectedConnector, mergeTargetNode, workflow, deletedNodes, nodeToBeDeleted, addNodeInBetween, insertNodeBetween, sidebarOpen, sidebarData, sidebarType, workflowForms } = useSelector((state) => state.connector)
    const { spaceId } = useParams();
    const { userInfo, userGroupSpaces } = useSelector(state => state.user);
    const { spaceSpecificUsers = [], spaceSpecificGroups = [], spaceSpecificUserPickers = [] } = useSelector((state) => state.userRoleGroupState);

    // Persist manual drag positions back to Redux so later edits (which rebuild the
    // canvas from workflow.nodes) don't revert a node to its original layout position.
    const handleNodeDragStop = useCallback((event, node, draggedNodes) => {
        const moved = (draggedNodes && draggedNodes.length ? draggedNodes : [node]).filter(Boolean);
        if (!moved.length) return;
        const posById = new Map(moved.map(n => [n.id, n.position]));
        const updatedNodes = (workflow.nodes || []).map(n =>
            posById.has(n.id) ? { ...n, position: { ...posById.get(n.id) } } : n
        );
        dispatch(setWorkflow({ ...workflow, nodes: updatedNodes }));
        dispatch(setChangeDetected(true));
    }, [workflow, dispatch]);

    const spaceData = getCurrentSpaceData(spaceId, userInfo?.spaceInfo, userGroupSpaces);

    useEffect(() => {
        dispatch(setSelectedStep({}))
    },[])
    // Suppress ResizeObserver errors that commonly occur with React Flow
    useEffect(() => {
        const handleResizeObserverError = (e) => {
            if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
                const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
                const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
                if (resizeObserverErr) {
                    resizeObserverErr.setAttribute('style', 'display: none');
                }
                if (resizeObserverErrDiv) {
                    resizeObserverErrDiv.setAttribute('style', 'display: none');
                }
            }
        };
        window.addEventListener('error', handleResizeObserverError);
        return () => window.removeEventListener('error', handleResizeObserverError);
    }, []);

    useEffect(() => {
        if (spaceData && spaceData.spaceId) {
            const breadCrumbItems = [
                {
                    title: <HomeOutlined />,
                    path: userInfo.isAdmin ? "/app/dashboard" : "/app/dashboard",
                },
                {
                    title: spaceData?.name,
                    path: `/app/space/${spaceId}/documents`
                },
                {
                    title: t("Workflow"),
                },
            ];
            setBreadCrumbs(breadCrumbItems)
        }
    }, [spaceData])

    const fetchNodeConditions = async (workflowId) => {
        showLoader(true)
        let response = await getWorkflowConditions(workflowId);
        showLoader(false)
        if (response.status === 200) {
            setWorkflowConditions(response.data)
        } else {
            setWorkflowConditions([])
        }
    }

    const getWorkflow = async () => {
        showLoader(true)
        const response = await getSpaceWorkflow(spaceId);
        showLoader(false)
        if (response.status === 200 && response.data?.workflowId) {
            setWorkflowData(response.data)
            dispatch(setWorkflow({ workflowId: response?.data?.workflowId || [], nodes: response?.data?.node || [], edges: response?.data?.edge || [] }))
            dispatch(setOriginalWorkflow({ nodes: response.data?.node || [], edges: response.data?.edge || [] }))
            await fetchNodeConditions(response.data.workflowId)
            return

        }
        setNodes([...initialNodes])
        setEdges([...initialEdges])
        dispatch(setWorkflow({ nodes: initialNodes, edges: initialEdges }))
        dispatch(setOriginalWorkflow({ nodes: initialNodes, edges: initialEdges }))

    }

    useEffect(() => {
        getWorkflow();
    }, [])

    useEffect(() => {
        if (userInfo?.userId && spaceId) {
            dispatch(fetchSpaceSpecificGroups({ spaceId, userInfo }, false));
            dispatch(fetchSpaceSpecificUserPickers({ spaceId, userInfo }, true));
            setTimeout(() => {
                dispatch(fetchSpaceSpecificUsers({ spaceId, userInfo }, false));
            }, 500);
        }
    }, [userInfo, dispatch, spaceId])

    // Always keep the latest graph in refs for consumers like the delete effect
    useEffect(() => { nodesRef.current = nodes }, [nodes])
    useEffect(() => { edgesRef.current = edges }, [edges])
    useEffect(() => { workflowDataRef.current = workflowData }, [workflowData])

    const fetchWorkflowForms = async () => {
        showLoader(true)
        let response = await getSpaceAllFormsByWorkflowId(workflowData.workflowId)
        showLoader(false)
        if (response.status !== 200) {
            message.error(t('No forms available'))
            dispatch(setWorkflowForms([]))
            return;
        }
        let currentForms = response.data || []
        dispatch(setWorkflowForms(currentForms))

    }

    useEffect(() => {
        if (workflowData.workflowId) {
            fetchWorkflowForms()
        }

    }, [workflowData.workflowId])

    // Important: never pass Redux (Immer-frozen) nodes/edges directly to React Flow state.
    // Clone them so React Flow can safely write measurement (width/height) during layout.
    // Sanitize helpers: strip React Flow runtime props (width/height/measured/etc.)
    // and return fresh, writable objects. Use these for both Redux->RF and RF->Redux syncs.
    const sanitizeNode = (n = {}) => {
        const clean = {
            id: n.id,
            type: n.type,
            position: n.position ? { x: n.position.x, y: n.position.y } : { x: 0, y: 0 },
            data: n.data ? { ...n.data } : {},
            formData: n.formData ? { ...n.formData } : {}
        };
        // allowlisted optional props (exclude width/height/measured/selected/dragging/etc.)
        if (n.draggable !== undefined) clean.draggable = n.draggable;
        if (n.selectable !== undefined) clean.selectable = n.selectable;
        if (n.style) clean.style = { ...n.style };
        if (n.className) clean.className = n.className;
        if (n.targetPosition) clean.targetPosition = n.targetPosition;
        if (n.sourcePosition) clean.sourcePosition = n.sourcePosition;
        if (n.hidden !== undefined) clean.hidden = n.hidden;
        if (n.zIndex !== undefined) clean.zIndex = n.zIndex;
        if (n.extent) clean.extent = n.extent;
        if (n.expandParent !== undefined) clean.expandParent = n.expandParent;
        if (n.parentId) clean.parentId = n.parentId;

        // Ensure proper individual dragging for step nodes
        if (clean.type === 'textInput' || clean.type === 'default') {
            clean.draggable = true;
            clean.selectable = true;
        } else if (clean.type === 'circle' && !clean.data?.type) {
            // Connector nodes stay fixed
            clean.draggable = true;
            clean.selectable = false;
        }

        return clean;
    };

    const sanitizeEdge = (e = {}) => ({ ...e });

    const buildDisplayGraph = (rawNodes = [], rawEdges = []) => {
        const displayNodes = rawNodes.map(sanitizeNode);
        const displayEdges = rawEdges.map(sanitizeEdge);

        const nodeById = new Map(displayNodes.map((n) => [n.id, n]));

        // --- Normalize Y positions so main-flow nodes sit on the same horizontal line ---
        // Build incoming count per node to detect branches
        const incomingCount = new Map();
        displayEdges.forEach((e) => {
            incomingCount.set(e.target, (incomingCount.get(e.target) || 0) + 1);
        });
        const targetIds = new Set(displayEdges.map((e) => e.target));
        const roots = displayNodes.filter((n) => !targetIds.has(n.id));
        const yNormalized = new Set();

        const normalizeChainY = (startNode, baseY) => {
            let cur = startNode;
            while (cur && !yNormalized.has(cur.id)) {
                cur.position = { ...cur.position, y: baseY };
                yNormalized.add(cur.id);
                // Only follow edges that stay on the main horizontal flow:
                // - Not the conditionb (No-branch) handle
                // - Only follow default (no handle) or conditiona (Yes-branch) edges
                const outEdges = displayEdges.filter((e) => {
                    if (e.source !== cur.id) return false;
                    if (e.sourceHandle === 'conditionb') return false;
                    // Only follow default edges (no sourceHandle) or conditiona (Yes branch)
                    return !e.sourceHandle || e.sourceHandle === 'conditiona';
                });
                if (outEdges.length === 0) break;
                const nextNode = nodeById.get(outEdges[0].target);
                if (!nextNode) break;
                // Don't normalize merge targets (nodes with multiple incoming edges)
                if ((incomingCount.get(nextNode.id) || 0) > 1) break;
                cur = nextNode;
            }
        };

        roots.forEach((root) => {
            normalizeChainY(root, root.position.y);
        });
        // --- End Y normalization ---
        const incomingMap = new Map();
        const outgoingMap = new Map();

        displayEdges.forEach((e) => {
            if (!incomingMap.has(e.target)) incomingMap.set(e.target, []);
            incomingMap.get(e.target).push(e);
            if (!outgoingMap.has(e.source)) outgoingMap.set(e.source, []);
            outgoingMap.get(e.source).push(e);
        });

        const visited = new Set();
        const chains = [];

        displayNodes.forEach((node) => {
            if (!VERIFICATION_TYPES.has(getNodeType(node)) || visited.has(node.id)) return;
            const incoming = incomingMap.get(node.id) || [];
            const hasIncomingFromVerification = incoming.some((edge) => {
                const src = nodeById.get(edge.source);
                return src && VERIFICATION_TYPES.has(getNodeType(src));
            });
            if (hasIncomingFromVerification) return;

            const chain = [];
            let cur = node;
            while (cur && VERIFICATION_TYPES.has(getNodeType(cur)) && !visited.has(cur.id)) {
                chain.push(cur);
                visited.add(cur.id);
                const outEdges = outgoingMap.get(cur.id) || [];
                const nextEdge = outEdges.find((edge) => {
                    const tgt = nodeById.get(edge.target);
                    return tgt && VERIFICATION_TYPES.has(getNodeType(tgt));
                });
                if (!nextEdge) break;
                cur = nodeById.get(nextEdge.target);
            }
            if (chain.length >= 2) chains.push(chain);
        });

        if (chains.length === 0) {
            return { nodes: displayNodes, edges: displayEdges };
        }

        const edgeIdsToHide = new Set();
        const displayOnlyEdges = [];
        const dedupe = new Set();

        chains.forEach((chain) => {
            const chainIds = new Set(chain.map((n) => n.id));
            const head = chain[0];
            const incomingFromNonVerification = displayEdges.find(
                (edge) => chainIds.has(edge.target) && !chainIds.has(edge.source)
            );
            if (incomingFromNonVerification) {
                const sourceNode = nodeById.get(incomingFromNonVerification.source);
                if (sourceNode?.position?.y !== undefined) {
                    head.position = { ...head.position, y: sourceNode.position.y };
                }
            }

            head.data = {
                ...head.data,
                verificationGroup: {
                    isGroupHead: true,
                    items: chain.map((item) => ({
                        id: item.id,
                        type: getNodeType(item),
                        name: getVerificationLabel(item)
                    }))
                }
            };

            chain.slice(1).forEach((item) => {
                item.data = {
                    ...item.data,
                    verificationGroup: { hidden: true, headId: head.id }
                };
                item.hidden = true;
            });

            displayEdges.forEach((edge) => {
                if (chainIds.has(edge.source) || chainIds.has(edge.target)) {
                    edgeIdsToHide.add(edge.id);
                }
            });

            const incoming = displayEdges.filter((edge) => chainIds.has(edge.target) && !chainIds.has(edge.source));
            incoming.forEach((edge) => {
                const key = `${edge.source}->${head.id}`;
                if (dedupe.has(key)) return;
                dedupe.add(key);
                displayOnlyEdges.push({
                    ...edge,
                    id: `display-${edge.id}-to-${head.id}`,
                    source: edge.source,
                    target: head.id,
                    data: { ...(edge.data || {}), displayOnly: true }
                });
            });

            const outgoing = displayEdges.filter((edge) => chainIds.has(edge.source) && !chainIds.has(edge.target));
            outgoing.forEach((edge) => {
                const key = `${head.id}->${edge.target}`;
                if (dedupe.has(key)) return;
                dedupe.add(key);
                displayOnlyEdges.push({
                    ...edge,
                    id: `display-${head.id}-to-${edge.id}`,
                    source: head.id,
                    target: edge.target,
                    data: { ...(edge.data || {}), displayOnly: true }
                });
            });
        });

        const updatedEdges = displayEdges.map((edge) => {
            if (edgeIdsToHide.has(edge.id)) {
                return { ...edge, hidden: true };
            }
            return edge;
        });

        return { nodes: displayNodes, edges: [...updatedEdges, ...displayOnlyEdges] };
    };

    useEffect(() => {
        if (Array.isArray(workflow.nodes) && workflow.nodes.length > 0) {
            // Provide React Flow with clean, writable node objects (visual grouping for verification nodes)
            const display = buildDisplayGraph(workflow.nodes, workflow.edges || []);
            setNodes(display.nodes);
            setEdges(display.edges);
        }
        if (Array.isArray(workflow.edges) && workflow.edges.length > 0) {
            // edges are already set above when nodes exist
        }
    }, [workflow, setNodes, setEdges])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (nodeToBeDeleted) {
            // Add a small delay to prevent ResizeObserver issues during rapid DOM changes
            const timeoutId = setTimeout(() => {
                // Read the latest nodes/edges from refs to avoid stale closures
                const curNodes = nodesRef.current || []
                const curEdges = edgesRef.current || []
                let prevEdge = curEdges.find(edge => edge.target === nodeToBeDeleted)
                let nextEdge = curEdges.find(edge => edge.source === nodeToBeDeleted);
                let deletingNode = curNodes.find(node => node.id === nodeToBeDeleted)

                // Guard: node must exist
                if (!deletingNode) {
                    return;
                }

                let updatedNodes = [...curNodes], updatedEdges = [...curEdges]
                let nodeIndex = curNodes.findIndex(node => node.id === nodeToBeDeleted)
                let deletingNodes = [nodeToBeDeleted], deletingEdges = []

                // Deleting the fork or the join removes the whole parallel region and
                // reconnects the surviving flow so nothing is left orphaned.
                if (deletingNode?.data?.parallelGroupId) {
                    const region = getParallelRegionToDelete(deletingNode, curNodes, curEdges)
                    if (region) {
                        const regionNodes = new Set(region.nodeIds)
                        const regionEdges = new Set(region.edgeIds)
                        let newNodes = curNodes.filter(n => !regionNodes.has(n.id))
                        let newEdges = curEdges.filter(e => !regionEdges.has(e.id))

                        const anchor = region.reconnectFromNodeId && newNodes.find(n => n.id === region.reconnectFromNodeId)
                        if (anchor) {
                            if (region.continuationTarget && newNodes.find(n => n.id === region.continuationTarget)) {
                                newEdges.push({ id: uuidv4(), source: anchor.id, target: region.continuationTarget, type: "figma" })
                            } else {
                                const newConnectorNode = createNode("connector", { ...anchor }, {}, {})
                                newNodes.push(newConnectorNode)
                                newEdges.push({ id: uuidv4(), source: anchor.id, target: newConnectorNode.id, type: "figma" })
                            }
                        }

                        setNodes(newNodes)
                        setEdges(newEdges)
                        dispatch(setWorkflow({ ...workflow, nodes: cloneNodesForRedux(newNodes), edges: cloneEdgesForRedux(newEdges) }))
                        dispatch(setDeletedNodes(deletedNodes.concat([...regionNodes])))
                        setDeletedFlowNodes((prev) => [...prev, ...regionNodes])
                        return
                    }
                }

                if (deletingNode?.type === "circle" && deletingNode?.data?.type === "end") {
                    // For end nodes, delete all related nodes without creating a new connector
                    let reqData = getNodeEdgeToBeDeleted(deletingNode, updatedEdges, updatedNodes)
                    updatedNodes = curNodes.filter(node => !reqData.nodes.includes(node.id))
                    updatedEdges = curEdges.filter(edge => !reqData.edges.includes(edge.id))

                    // Find the node that was connected before the deleted end node
                    let lastNode = prevEdge ? updatedNodes.find(n => n.id === prevEdge.source) : updatedNodes[updatedNodes.length - 1]
                    if (lastNode && prevEdge) {
                        let newConnectorNode = createNode("connector", { ...lastNode }, {}, prevEdge)
                        updatedNodes.push(newConnectorNode)
                        // Update the previous edge to point to the new connector
                        updatedEdges = updatedEdges.map(edge => {
                            if (edge.id === prevEdge.id) {
                                return { ...edge, target: newConnectorNode.id }
                            }
                            return { ...edge };
                        })
                    }
                    deletingNodes = [...reqData.nodes];
                    deletingEdges = deletingEdges.concat(curEdges.filter(edge => reqData.edges.includes(edge.id)));
                } else {
                    // General cases for other nodes (step, notification, httpRequest, connector, etc.)
                    // Using imported SHIFT_CIRCLE and SHIFT_STEP from createEdgeNode.js

                    const isPlainConnector = deletingNode?.type === "circle" && !deletingNode?.data?.type
                    const sourceOutCount = prevEdge ? curEdges.filter(e => e.source === prevEdge.source).length : 0

                    if (isPlainConnector && prevEdge && !nextEdge && sourceOutCount > 1) {
                        // Deleting an empty parallel-branch connector: just drop the connector and its
                        // incoming edge, leaving the other branches off the fork untouched.
                        updatedNodes = updatedNodes.filter(n => n.id !== nodeToBeDeleted)
                        updatedEdges = curEdges.filter(e => e.id !== prevEdge.id)
                        deletingEdges = [{ ...prevEdge }]
                    } else if (!prevEdge && nextEdge) {
                        // Deleting the first node in the chain: remove the outgoing edge and the node, shift others left
                        updatedEdges = curEdges.filter(edge => edge.id !== nextEdge.id);
                        deletingEdges = [{ ...nextEdge }];

                        updatedNodes = updatedNodes
                            .map((node, index) => {
                                if (index > nodeIndex) {
                                    return {
                                        ...node,
                                        position: { x: node.position.x - (deletingNode.type === 'circle' ? SHIFT_CIRCLE : SHIFT_STEP), y: node.position.y }
                                    };
                                }
                                return { ...node };
                            })
                            .filter(node => node.id !== nodeToBeDeleted);
                    } else if (prevEdge && !nextEdge) {
                        // Deleting the last node: connect previous to a fresh connector appropriate to type
                        const prevNode = curNodes.find(n => n.id === prevEdge.source);
                        let connectorType = deletingNode.type === 'circle' ? 'notificationConnector' : 'connector';
                        const newConnectorNode = createNode(connectorType, { ...prevNode }, {}, prevEdge);

                        updatedNodes = updatedNodes.filter(n => n.id !== nodeToBeDeleted).concat(newConnectorNode);
                        updatedEdges = curEdges.map(edge => {
                            if (edge.id === prevEdge.id) return { ...edge, target: newConnectorNode.id };
                            return { ...edge };
                        });
                        // no nextEdge to delete
                    } else if (!prevEdge && !nextEdge) {
                        // Isolated node: just remove it
                        updatedNodes = updatedNodes.filter(n => n.id !== nodeToBeDeleted);
                        updatedEdges = [...curEdges];
                    } else {
                        // Middle node with both edges: bridge prev->next and remove nextEdge, shift right-hand nodes left
                        updatedEdges = curEdges.map(edge => {
                            if (edge.id === prevEdge.id) return { ...edge, target: nextEdge.target };
                            return { ...edge };
                        });

                        updatedNodes = updatedNodes
                            .map((node, index) => {
                                if (index > nodeIndex) {
                                    return {
                                        ...node,
                                        position: { x: node.position.x - (deletingNode.type === 'circle' ? SHIFT_CIRCLE : SHIFT_STEP), y: node.position.y }
                                    };
                                }
                                return { ...node };
                            })
                            .filter(node => node.id !== nodeToBeDeleted);

                        updatedEdges = updatedEdges.filter(edge => edge.id !== nextEdge.id);
                        deletingEdges = [{ ...nextEdge }];
                    }
                }
                // Prune any redundant/stray connector (+) nodes (no incident edges)
                const pruneConnectors = (nodesArr, edgesArr) => {
                    const nodeById = new Map(nodesArr.map(n => [n.id, n]))
                    const isConnector = (n) => n?.type === 'circle' && (!n.data || !n.data.type)

                    // First, collapse consecutive connectors: A(conn) -> B(conn)
                    let edgesWork = edgesArr.map(e => ({ ...e }))
                    const toDeleteNodes = new Set()

                    for (const e of edgesArr) {
                        const src = nodeById.get(e.source)
                        const tgt = nodeById.get(e.target)
                        if (isConnector(src) && isConnector(tgt)) {
                            // Redirect all incoming edges of src to tgt and remove src
                            edgesWork.forEach(ie => {
                                if (ie.target === e.source) ie.target = e.target
                            })
                            toDeleteNodes.add(e.source)
                        }
                    }

                    let nodesWork = nodesArr.filter(n => !toDeleteNodes.has(n.id))
                    let edgesAfterCollapse = edgesWork.filter(e => !toDeleteNodes.has(e.source) && !toDeleteNodes.has(e.target))

                    // Remove isolated connectors (0 in and 0 out)
                    const inCount = new Map();
                    const outCount = new Map();
                    edgesAfterCollapse.forEach(e => {
                        outCount.set(e.source, (outCount.get(e.source) || 0) + 1)
                        inCount.set(e.target, (inCount.get(e.target) || 0) + 1)
                    })

                    nodesWork = nodesWork.filter(n => {
                        if (!isConnector(n)) return true
                        const inc = inCount.get(n.id) || 0
                        const out = outCount.get(n.id) || 0
                        return !(inc === 0 && out === 0)
                    })

                    const validIds = new Set(nodesWork.map(n => n.id))
                    const prunedEdges = edgesAfterCollapse.filter(e => validIds.has(e.source) && validIds.has(e.target))
                    return { prunedNodes: nodesWork, prunedEdges }
                }

                const { prunedNodes, prunedEdges } = pruneConnectors(updatedNodes, updatedEdges)

                // Batch state updates to prevent ResizeObserver issues
                setNodes(prunedNodes)
                setEdges(prunedEdges)
                setDeletedEdges(prev => [...prev, ...deletingEdges])
                dispatch(setWorkflow({ ...workflow, nodes: cloneNodesForRedux(prunedNodes), edges: cloneEdgesForRedux(prunedEdges) }))
                dispatch(setDeletedNodes(deletedNodes.concat(deletingNodes)))
                setDeletedFlowNodes((prev) => [...prev, ...deletingNodes])
            }, 10); // Small delay to allow DOM to settle

            // Cleanup timeout on unmount or dependency change
            return () => clearTimeout(timeoutId);
        }
    }, [nodeToBeDeleted])

    const filterPrevEdge = (edges, prevEdge) => {
        return edges.filter(edge => edge.id !== prevEdge.id)
    }

    // Clone+sanitize helpers to avoid leaking React Flow runtime props into Redux (which Immer freezes)
    const stripDisplayOnlyData = (data = {}) => {
        const cleaned = { ...data };
        delete cleaned.verificationGroup;
        return cleaned;
    };
    const cloneNodesForRedux = (arr = []) => arr.map((node) => {
        const clean = sanitizeNode(node);
        clean.data = stripDisplayOnlyData(clean.data);
        if (clean.hidden !== undefined) delete clean.hidden;
        return clean;
    });
    const cloneEdgesForRedux = (arr = []) =>
        arr
            .filter((edge) => !(edge?.data && edge.data.displayOnly))
            .map((edge) => {
                const clean = sanitizeEdge(edge);
                if (clean.hidden !== undefined) delete clean.hidden;
                if (clean.data && clean.data.displayOnly) {
                    const { displayOnly, ...rest } = clean.data;
                    clean.data = rest;
                }
                return clean;
            });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (nodeType && selectedConnector.id) {
            // Add validation for workflow state and prevent stale closure issues
            if (!nodes || !edges || nodes.length === 0) {
                return;
            }

            let connectorNode = nodes.find(node => node.id === selectedConnector.id)
            if (!connectorNode) {
                return;
            }
            if (connectorNode.data?.type === "start") {
                dispatch(resetNodeType())
                dispatch(setWorkflow({ ...workflow, nodes: cloneNodesForRedux(defaultNodes), edges: cloneEdgesForRedux(initialEdges) }))
                dispatch(setChangeDetected(true))
                return
            }
            let prevCurrentEdge = edges.find(edge => edge.target === connectorNode.id);
            if (!prevCurrentEdge) {
                return;
            }

            let prevNode = nodes.find(node => node.id === prevCurrentEdge.source);
            if (!prevNode) {
                return;
            }

            let updatedNodes = nodes.filter(node => node.id !== connectorNode.id)
            let updatedEdges = [...edges]

            let hasCondition = checkNodeCondition(prevNode, workflowConditions || [], prevCurrentEdge)

            if (!hasCondition) {
                message.error(t('Please add condition to proceed'));
                return
            }

            if (nodeType === 'step' || nodeType === "approvalStep") {
                let phaseNode = createNode(nodeType, prevNode, connectorNode, prevCurrentEdge)
                phaseNode = { ...phaseNode, data: {...phaseNode.data, name: nodeType === 'approvalStep' ? 'Approval Step' : 'Workflow Step', approvalStep: nodeType === 'approvalStep' ? true : false, stepColor: nodeType === 'approvalStep' ? "#0A99FE" : '#EB2F96', stepIcon: nodeType === 'approvalStep' ? "checkOutlined" : "fileEdit",} };

                let newConnectorNode = createNode("connector", { ...phaseNode }, {}, prevCurrentEdge)
                // Create a clean connector node without React Flow's internal properties
                const cleanConnectorNode = {
                    id: connectorNode.id,
                    type: newConnectorNode.type,
                    position: { ...newConnectorNode.position },
                    data: { ...newConnectorNode.data },
                    draggable: newConnectorNode.draggable
                }

                updatedNodes.push({ ...phaseNode, data: { ...phaseNode.data } }, cleanConnectorNode)
                let sourceEdge = { ...prevCurrentEdge, target: phaseNode.id }
                // createEdge(nodeType, prevNode, phaseNode, "Y", prevCurrentEdge)
                let targetEdge = { id: uuidv4(), source: phaseNode.id, target: cleanConnectorNode.id, type: "figma" }

                updatedEdges = filterPrevEdge(updatedEdges, prevCurrentEdge)
                updatedEdges.push(sourceEdge, targetEdge)
                dispatch(setSelectedStep({ name: phaseNode.data.name, assignTo: { 'user': [], role: [] }, approvalStep: phaseNode.data.approvalStep, deadline: false, id: phaseNode.id }))
                dispatch(setSelectedConnector({}))

            } else if (nodeType === "estimation") {
                let notificationNode = createNode("estimation", prevNode, connectorNode, prevCurrentEdge)
                let newConnectorNode = createNode("notificationConnector", notificationNode, {}, prevCurrentEdge)

                // Create a clean connector node without React Flow's internal properties
                const cleanConnectorNode = {
                    id: connectorNode.id,
                    type: newConnectorNode.type,
                    position: { ...newConnectorNode.position },
                    data: { ...newConnectorNode.data },
                    draggable: newConnectorNode.draggable
                }

                updatedNodes.push(notificationNode, cleanConnectorNode)

                let sourceEdge = { ...prevCurrentEdge, target: notificationNode.id }
                let targetEdge = createEdge(nodeType, notificationNode, cleanConnectorNode, prevCurrentEdge)
                updatedEdges = filterPrevEdge(updatedEdges, prevCurrentEdge)
                updatedEdges.push(sourceEdge, targetEdge)
                dispatch(setSelectedStep({}))
                dispatch(setSelectedConnector({}))
                dispatch(setConnectorMenu("estimation"))

            } else if (nodeType === "condition") {
                let conditionNode = createNode(nodeType, prevNode, connectorNode, prevCurrentEdge)
                let connectorYesNode = createNode("conditionConnectorYes", conditionNode, {}, "Y")
                let connectorNoNode = createNode("conditionConnectorNo", conditionNode, connectorNode, "N")

                // Create a clean connector node for the Yes branch
                const cleanConnectorYesNode = {
                    id: connectorNode.id,
                    type: connectorYesNode.type,
                    position: { ...connectorYesNode.position },
                    data: { ...connectorYesNode.data },
                    draggable: connectorYesNode.draggable
                }

                updatedNodes.push(conditionNode, cleanConnectorYesNode, connectorNoNode)

                let sourceEdge = { ...prevCurrentEdge, target: conditionNode.id }
                // createEdge("step", prevNode, conditionNode)
                let edgeYes = createEdge("condition", conditionNode, cleanConnectorYesNode, "Y")
                let edgeNo = createEdge("condition", conditionNode, connectorNoNode, "N")

                updatedEdges = filterPrevEdge(updatedEdges, prevCurrentEdge)

                updatedEdges.push(sourceEdge, { ...edgeYes, type: 'buttonedge', label: t('If Yes'), target: cleanConnectorYesNode.id }, { ...edgeNo, type: 'buttonedge', label: t('If No') })
                dispatch(setSelectedStep({ id: conditionNode.id, type: 'condition' }))
                dispatch(setSelectedConnector({}))
                dispatch(setConnectorMenu("condition"))

            } else if (nodeType === "notification") {
                let notificationNode = createNode(nodeType, prevNode, connectorNode, prevCurrentEdge);
                let newConnectorNode = createNode("notificationConnector", notificationNode, {}, prevCurrentEdge)

                // Create a clean connector node without React Flow's internal properties
                const cleanConnectorNode = {
                    id: connectorNode.id,
                    type: newConnectorNode.type,
                    position: { ...newConnectorNode.position },
                    data: { ...newConnectorNode.data },
                    draggable: newConnectorNode.draggable
                }

                updatedNodes.push(notificationNode, cleanConnectorNode)
                let sourceEdge = { ...prevCurrentEdge, target: notificationNode.id }
                let targetEdge = createEdge(nodeType, notificationNode, cleanConnectorNode, prevCurrentEdge)
                updatedEdges = filterPrevEdge(updatedEdges, prevCurrentEdge)

                updatedEdges.push(sourceEdge, targetEdge)
                dispatch(setSelectedStep({
                    id: notificationNode.id,
                    data: {
                        type: "notification",
                        name: "Notification",
                        subject: "",
                        message: "",
                        notifyTo: { user: [], role: [], group: [], userPicker: [] }
                    }
                }))
                dispatch(setSelectedConnector({}))
                dispatch(setConnectorMenu("notification"))

            } else if (nodeType === 'httpRequest') {
                const httpNode = createNode('httpRequest', prevNode, connectorNode, prevCurrentEdge);
                const newConnectorNode = createNode('notificationConnector', httpNode, {}, prevCurrentEdge);

                const cleanConnectorNode = {
                    id: connectorNode.id,
                    type: newConnectorNode.type,
                    position: { ...newConnectorNode.position },
                    data: { ...newConnectorNode.data },
                    draggable: newConnectorNode.draggable,
                };

                updatedNodes.push(httpNode, cleanConnectorNode);
                const sourceEdge = { ...prevCurrentEdge, target: httpNode.id };
                const targetEdge = createEdge('httpRequest', httpNode, cleanConnectorNode, prevCurrentEdge);
                updatedEdges = filterPrevEdge(updatedEdges, prevCurrentEdge);
                updatedEdges.push(sourceEdge, targetEdge);

                // Select the node to open details panel
                dispatch(setSelectedStep({ id: httpNode.id, data: { type: 'httpRequest' } }));
                dispatch(setSelectedConnector({}));

            } else if (nodeType === 'sharePublicForm' || nodeType === "duplicateCheck" || nodeType === "outlookCalendar" || nodeType === "googlePlaces") {
                const vType = nodeType; // verification type

                // If prevNode is a verification group head, resolve to the actual last
                // node in the chain so the new node appends at the end of the group
                // instead of branching off the head and splitting the group.
                let actualPrev = prevNode;
                let actualPrevEdge = prevCurrentEdge;
                if (prevNode.data?.verificationGroup?.isGroupHead) {
                    const groupItems = prevNode.data.verificationGroup.items;
                    const lastItemId = groupItems[groupItems.length - 1].id;
                    const lastNode = nodes.find(n => n.id === lastItemId);
                    if (lastNode) {
                        // Find the real (non-display-only) edge going out of the last node
                        const realEdge = edges.find(e => e.source === lastItemId && !(e.data?.displayOnly));
                        if (realEdge) {
                            actualPrev = lastNode;
                            actualPrevEdge = realEdge;
                        }
                    }
                }

                const vNode = createNode(vType, actualPrev, connectorNode, actualPrevEdge);
                const newConnectorNode = createNode('notificationConnector', vNode, {}, actualPrevEdge);

                let cleanConnectorNode = {
                    id: connectorNode.id,
                    type: newConnectorNode.type,
                    position: { ...newConnectorNode.position },
                    data: { ...newConnectorNode.data },
                    draggable: newConnectorNode.draggable,
                };
                if (nodeType === "duplicateCheck" || nodeType === "outlookCalendar" || nodeType === "googlePlaces") {
                    cleanConnectorNode = { ...cleanConnectorNode, position: { x: newConnectorNode.position.x + 100, y: newConnectorNode.position.y } }
                }

                updatedNodes.push(vNode, cleanConnectorNode);
                const sourceEdge = { ...actualPrevEdge, target: vNode.id };
                const targetEdge = createEdge(vType, vNode, cleanConnectorNode, actualPrevEdge);
                updatedEdges = filterPrevEdge(updatedEdges, actualPrevEdge);
                // Also remove the display-only edge if we resolved through a group
                if (actualPrevEdge.id !== prevCurrentEdge.id) {
                    updatedEdges = filterPrevEdge(updatedEdges, prevCurrentEdge);
                }
                updatedEdges.push(sourceEdge, targetEdge);

                // select node to open details panel
                dispatch(setSelectedStep({ id: vNode.id, data: { type: vType } }));
                dispatch(setSelectedConnector({}));

            } else if (nodeType === "end") {
                let endNode = createNode("end", prevNode, connectorNode, prevCurrentEdge)
                let approvedNode = createNode("approve", endNode)
                let rejectedNode = createNode("reject", endNode)
                updatedNodes = updatedNodes.filter(node => node.id !== connectorNode.id)

                // remove custom notification node
                // updatedNodes.push(endNode, approvedNode, rejectedNode)
                updatedNodes.push(endNode)

                let sourceEdge = { ...prevCurrentEdge, target: endNode.id }
                let flagToApproveEdge = createEdge("approve", endNode, approvedNode, prevCurrentEdge)
                let flagToRejectEdge = createEdge("reject", endNode, rejectedNode, prevCurrentEdge)

                // let approveConnectorEdge = createEdge("step", approvedNode, connectorApproveNode)
                // let rejectConnectorEdge = createEdge("step", rejectedNode, connectorRejectNode)

                updatedEdges = filterPrevEdge(updatedEdges, prevCurrentEdge)

                // updatedEdges.push(sourceEdge, flagToApproveEdge, flagToRejectEdge)
                updatedEdges.push(sourceEdge)

                dispatch(setSelectedStep({}))
                dispatch(setSelectedConnector({}))
                dispatch(setConnectorMenu("end"))
                dispatch(setDeletedNodes(deletedNodes.concat(connectorNode.id)))
                setDeletedFlowNodes((prev) => [...prev, connectorNode.id])

            } else if (nodeType === "parallelBranch") {
                // Resolve the fork. If the clicked connector's chain already belongs to a parallel
                // group (and sits before its join), add the new branch as a sibling off that group's
                // fork. Otherwise fork off the node immediately preceding the connector (new group).
                let fork = prevNode
                let useExistingGroup = false
                const existingGroupId = findParallelGroupId(prevNode.id, nodes, edges)
                if (existingGroupId) {
                    const groupFork = nodes.find(n => n.data?.parallelGroupId === existingGroupId && n.data?.type !== "join")
                    const joinNode = findJoinNodeForGroup(existingGroupId, nodes)
                    // Is the join an ancestor of prevNode? If so, prevNode is past the merge -> new group.
                    let pastJoin = false
                    if (joinNode) {
                        const visited = new Set()
                        const isAncestor = (id) => {
                            if (id === joinNode.id) return true
                            if (visited.has(id)) return false
                            visited.add(id)
                            return edges.filter(e => e.target === id).some(e => isAncestor(e.source))
                        }
                        pastJoin = isAncestor(prevNode.id)
                    }
                    if (groupFork && !pastJoin) {
                        fork = groupFork
                        useExistingGroup = true
                    }
                }
                const groupId = useExistingGroup ? existingGroupId : (fork.data?.parallelGroupId || uuidv4())

                // Tag the fork with the group id and label the primary (untagged) edge as the first branch
                updatedNodes = nodes.map(node =>
                    node.id === fork.id ? { ...node, data: { ...node.data, parallelGroupId: groupId } } : { ...node }
                )
                updatedEdges = edges.map(edge =>
                    (edge.source === fork.id && !edge.data?.branchId)
                        ? { ...edge, data: { ...(edge.data || {}), parallelGroupId: groupId, branchId: uuidv4(), branchLabel: "Branch 1" } }
                        : { ...edge }
                )

                // Align the new branch with existing siblings and stack it below the lowest one.
                const branchTargets = edges.filter(e => e.source === fork.id)
                    .map(e => nodes.find(n => n.id === e.target)).filter(Boolean)
                const branchStepX = branchTargets.length ? Math.min(...branchTargets.map(n => n.position.x)) : connectorNode.position.x
                const lowestBranchY = branchTargets.length ? Math.max(...branchTargets.map(n => n.position.y)) : fork.position.y
                const branchCount = branchTargets.length

                const branchStep = {
                    id: uuidv4(), type: "textInput", draggable: true,
                    position: { x: branchStepX, y: lowestBranchY + BRANCH_Y_GAP },
                    data: { name: "Workflow Step", approvalStep: false, stepColor: "#EB2F96", stepIcon: "fileEdit" }
                }
                const branchConnector = {
                    id: uuidv4(), type: "circle", draggable: false,
                    position: { x: branchStep.position.x + 317, y: branchStep.position.y }, data: {}
                }
                const forkToBranch = {
                    id: uuidv4(), source: fork.id, target: branchStep.id, type: "figma",
                    data: { parallelGroupId: groupId, branchId: uuidv4(), branchLabel: `Branch ${branchCount + 1}` }
                }
                const branchToConnector = { id: uuidv4(), source: branchStep.id, target: branchConnector.id, type: "figma" }

                updatedNodes = [...updatedNodes, branchStep, branchConnector]
                updatedEdges = [...updatedEdges, forkToBranch, branchToConnector]
                // Re-center all of the fork's branches symmetrically around its Y.
                updatedNodes = relayoutParallelBranches(fork.id, updatedNodes, updatedEdges, BRANCH_Y_GAP)
                dispatch(setSelectedStep({ name: branchStep.data.name, assignTo: { user: [], role: [] }, approvalStep: false, deadline: false, id: branchStep.id }))
                dispatch(setSelectedConnector({}))

            } else if (nodeType === "mergeFlow") {
                // Merge the branch whose terminal + connector was clicked into the group's join node.
                const branchInfo = findBranchInfo(prevNode.id, nodes, edges)
                const groupId = branchInfo?.parallelGroupId || findParallelGroupId(prevNode.id, nodes, edges)
                if (!groupId) {
                    message.error(t('Merge is only available inside a parallel branch'))
                    return
                }
                let joinNode = findJoinNodeForGroup(groupId, nodes)
                let createdJoin = false
                if (!joinNode) {
                    const fork = nodes.find(n => n.data?.parallelGroupId === groupId && n.data?.type !== "join")
                    const joinY = fork ? fork.position.y : prevNode.position.y
                    joinNode = createJoinNode(groupId, { x: connectorNode.position.x + 80, y: joinY })
                    createdJoin = true
                }
                updatedEdges = updatedEdges.map(edge =>
                    edge.id === prevCurrentEdge.id
                        ? {
                            ...edge, target: joinNode.id, type: "figma",
                            data: { ...(edge.data || {}), parallelGroupId: groupId, branchId: branchInfo?.branchId, branchLabel: branchInfo?.branchLabel }
                        }
                        : { ...edge }
                )
                if (createdJoin) {
                    // give the new join a trailing + connector so the main flow can continue past the merge
                    const joinConnector = {
                        id: uuidv4(), type: "circle", draggable: false,
                        position: { x: joinNode.position.x + 200, y: joinNode.position.y }, data: {}
                    }
                    updatedNodes = [...updatedNodes, joinNode, joinConnector]
                    updatedEdges = [...updatedEdges, { id: uuidv4(), source: joinNode.id, target: joinConnector.id, type: "figma" }]
                }
                dispatch(setSelectedStep({}))
                dispatch(setSelectedConnector({}))
            }
            setNodes(updatedNodes)
            setEdges(updatedEdges)
            dispatch(resetNodeType())
            dispatch(setWorkflow({ ...workflow, nodes: cloneNodesForRedux(updatedNodes), edges: cloneEdgesForRedux(updatedEdges) }))
            dispatch(setChangeDetected(true))

        }
    }, [nodeType, selectedConnector])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (addNodeInBetween.side) {
            let currentNode = nodes.find(node => node.id === addNodeInBetween.id);
            let requiredEdge = {}, reqNode = {}
            let newNode = { id: uuidv4(), draggable: true, position: { x: getNodeXPosition(currentNode, addNodeInBetween.side), y: getNodeYPosition(currentNode) }, data: { name: "" }, type: "textInput" }
            if (addNodeInBetween.side === "right") {
                requiredEdge = edges.find(edge => edge.source === addNodeInBetween.id)
                reqNode = nodes.find(node => node.id === requiredEdge.target)
            } else if (addNodeInBetween.side === "left") {
                requiredEdge = edges.find(edge => edge.target === addNodeInBetween.id)
                reqNode = nodes.find(node => node.id === requiredEdge.source)

            }
            let updatedNodes = sortNodesIndexWise(nodes)
            let currentNodeIndex = updatedNodes.findIndex(node => node.id === addNodeInBetween.id)
            let reqIndex = addNodeInBetween.side === "right" ? currentNodeIndex + 1 : currentNodeIndex
            updatedNodes.splice(reqIndex, 0, newNode)
            updatedNodes = updatedNodes.map((node, index) => {
                if (index > reqIndex) {
                    return { ...node, position: { x: node.position.x + NODE_SPACING, y: node.position.y } }
                } else {
                    return { ...node }
                }
            })
            let reqEdgeKey = addNodeInBetween.side === "left" ? "source" : "target"
            let newEdge = {}
            if (addNodeInBetween.side === "right") {
                newEdge = { id: uuidv4(), source: newNode.id, target: reqNode.id, type: "figma" }
            } else {
                newEdge = { id: uuidv4(), source: reqNode.id, target: newNode.id, type: "figma" }
            }
            let updatedEdges = edges.map(edge => {
                if (edge.id === requiredEdge.id) {
                    return { ...edge, [reqEdgeKey]: newNode.id }
                } else {
                    return { ...edge }
                }
            })

            updatedEdges.push(newEdge)
            setNodes(updatedNodes)
            setEdges(updatedEdges)
            dispatch(resetNodeType())
            dispatch(setWorkflow({ ...workflow, nodes: cloneNodesForRedux(updatedNodes), edges: cloneEdgesForRedux(updatedEdges) }))
            dispatch(setChangeDetected(true))
            dispatch(setAddNodeInBetween({}))

        }
    }, [addNodeInBetween])

    // Handler for edge hover plus button insertion
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // Only process if we have a nodeType (meaning user selected from sidebar)
        if (insertNodeBetween.edgeId && insertNodeBetween.position && insertNodeBetween.nodeType) {

            // Find the edge to split
            let edgeToSplit = edges.find(edge => edge.id === insertNodeBetween.edgeId);
            if (!edgeToSplit) {
                dispatch(setInsertNodeBetween({}));
                return;
            }

            // If the edge is a display-only edge from a verification group head,
            // resolve to the real edge from the last node in the chain so we
            // append to the end of the group instead of branching off the head.
            if (edgeToSplit.data?.displayOnly) {
                const edgeSourceNode = nodes.find(n => n.id === edgeToSplit.source);
                if (edgeSourceNode?.data?.verificationGroup?.isGroupHead) {
                    const groupItems = edgeSourceNode.data.verificationGroup.items;
                    const lastItemId = groupItems[groupItems.length - 1].id;
                    // Find the real (non-display-only) outgoing edge from the last chain node
                    // that leads to a non-verification target (i.e. the edge leaving the group)
                    const realEdge = edges.find(e =>
                        e.source === lastItemId &&
                        !(e.data?.displayOnly) &&
                        !VERIFICATION_TYPES.has(getNodeType(nodes.find(n => n.id === e.target)))
                    );
                    if (realEdge) {
                        edgeToSplit = realEdge;
                    }
                }
            }

            // Find source and target nodes
            const sourceNode = nodes.find(node => node.id === edgeToSplit.source);
            const targetNode = nodes.find(node => node.id === edgeToSplit.target);

            if (!sourceNode || !targetNode) {
                dispatch(setInsertNodeBetween({}));
                return;
            }

            // Create new node at the specified position honoring tool selection
            const normalize = (t) => (t || '').toString().toLowerCase().replace(/\s+/g, '');
            const rawType = insertNodeBetween.nodeType || 'step';
            const norm = normalize(rawType);
            const canonical = (
                norm === 'httprequest' ? 'httpRequest' :
                        norm === 'outlookcalendar' ? 'outlookCalendar' :
                        norm === 'googleplaces' ? 'googlePlaces' :
                                    (norm === 'sharepublicform' || norm === 'sharepublic' || norm === 'publicformshare') ? 'sharePublicForm' :
                                        norm === 'duplicatecheck' ? 'duplicateCheck' :
                                                norm === 'notification' ? 'notification' :
                                                    norm === 'estimation' ? 'estimation' :
                                                        norm === 'end' ? 'end' :
                                                            norm === 'approvalstep' ? 'approvalStep' :
                                                                'step'
            );

            let nodeData = { name: "step", stepColor: "#0A99FE", stepIcon: "fileEdit" };
            let nodeTypeToUse = 'textInput'; // default to full card renderer

            switch (canonical) {
                case 'step':
                    nodeData = { name: 'Workflow Step', stepColor: "#EB2F96", stepIcon: "fileEdit" };
                    break;
                case 'approvalStep':
                    nodeData = { name: 'Approval Step', approvalStep: true, stepColor: "#0A99FE", stepIcon: "checkOutlined", data: {approvalStep: true} };
                    break;
                case 'end':
                    nodeData = { name: 'End', type: 'end', stepColor: "#52c41a", stepIcon: "complete" };
                    break;
                case 'notification':
                    // Keep as compact connector style: render using circle node type
                    nodeData = { name: 'Notification', type: 'notification', stepColor: "#faad14", stepIcon: "notification" };
                    nodeTypeToUse = 'circle';
                    break;
                case 'httpRequest':
                    nodeData = { name: 'HTTP Request', type: 'httpRequest', stepColor: "#722ed1", stepIcon: "api" };
                    nodeTypeToUse = 'circle';
                    break;
                case 'outlookCalendar':
                    nodeData = { name: 'Outlook Calendar', type: 'outlookCalendar', stepColor: "#0ea5e9", stepIcon: "outlookCalendar" };
                    nodeTypeToUse = 'circle';
                    break;
                case 'googlePlaces':
                    nodeData = { name: 'Google Places', type: 'googlePlaces', stepColor: "#0ea5e9", stepIcon: "location" };
                    nodeTypeToUse = 'circle';
                    break;
                case 'sharePublicForm':
                    nodeData = { name: 'Share Public Form', type: 'sharePublicForm', stepColor: "#6366f1", stepIcon: "share" };
                    nodeTypeToUse = 'circle';
                    break;
                case 'duplicateCheck':
                    nodeData = { name: 'Duplicate Check', type: 'duplicateCheck', stepColor: "#0ea5e9", stepIcon: "calendar" };
                    nodeTypeToUse = 'circle';
                    break;
                case 'estimation':
                    nodeData = { name: 'Estimation', type: 'estimation', stepColor: "#f59e0b", stepIcon: "calculator" };
                    nodeTypeToUse = 'circle';
                    break;
                default:
                    nodeData = { name: 'New Step', stepColor: "#1890FF", stepIcon: "fileEdit" };
            }

            // Check if inserting a verification node next to existing verification nodes
            const isVerificationType = VERIFICATION_TYPES.has(canonical);
            const sourceIsVerification = VERIFICATION_TYPES.has(getNodeType(sourceNode));
            const targetIsVerification = VERIFICATION_TYPES.has(getNodeType(targetNode));
            const adjacentVerification = isVerificationType && (sourceIsVerification || targetIsVerification);

            // Calculate required width for the new node
            const requiredWidth = nodeTypeToUse === "circle" ? CONNECTOR_SPACING : NODE_SPACING;

            // Calculate the available space between source and target
            const availableSpace = targetNode.position.x - sourceNode.position.x;

            // Skip shifting when adding verification node next to existing verification nodes
            // since buildDisplayGraph will collapse them into a visual group
            const needsShift = adjacentVerification ? false : availableSpace < (requiredWidth * 2);

            // Position the new node
            let newNodeX;
            if (adjacentVerification) {
                // Place at same position as the adjacent verification node — they'll be grouped visually
                newNodeX = sourceIsVerification ? sourceNode.position.x : targetNode.position.x;
            } else if (needsShift) {
                // Not enough space - place after source and we'll shift target
                newNodeX = sourceNode.type === "circle" ? sourceNode.position.x + CONNECTOR_SPACING : sourceNode.position.x + NODE_SPACING;
            } else {
                // Enough space - place in the middle of the gap
                newNodeX = sourceNode.position.x + Math.floor(availableSpace / 2);
            }

            const newNode = {
                id: uuidv4(),
                type: nodeTypeToUse,
                position: {
                    x: newNodeX,
                    y: sourceNode.position.y,
                },
                data: nodeData,
                draggable: true
            };

            // Create new edges based on node type
            let updatedNodes = [...nodes, newNode];
            // let updatedEdges = edges.filter(edge => edge.id !== edgeToSplit.id); // Remove original edge
            let updatedEdges = [...edges]

            if (canonical === 'end') {
                // For end nodes, just connect source -> newNode (stop the chain here)
                const firstEdge = {
                    id: uuidv4(),
                    type: 'figma',
                    source: edgeToSplit.source,
                    target: newNode.id
                };
                updatedEdges = updatedEdges.concat([firstEdge]);
            } else {
                // For all other nodes, insert strictly between source and target (no extra connector)

                const secondEdge = {
                    id: uuidv4(),
                    type: 'figma',
                    source: newNode.id,
                    target: edgeToSplit.target
                };

                let nodesWithConnectorPositionChange;

                if (needsShift) {
                    // Not enough space - shift all nodes at or to the right of the target
                    const additionalWidth = newNode.type === "circle" ? CONNECTOR_SPACING : NODE_SPACING;
                    const targetX = targetNode.position.x;

                    nodesWithConnectorPositionChange = nodes.map((node) => {
                        if (node.position.x >= targetX) {
                            return { ...node, position: { x: node.position.x + additionalWidth, y: node.position.y } }
                        }
                        return { ...node }
                    })
                } else {
                    // Enough space - no shifting needed, just insert the node
                    nodesWithConnectorPositionChange = [...nodes];
                }

                updatedNodes = [...nodesWithConnectorPositionChange, newNode];
                updatedEdges = updatedEdges.map(edge => {
                    if (edge.id === edgeToSplit.id) {
                        return { ...edgeToSplit, target: newNode.id }
                    }
                    return { ...edge }
                })
                updatedEdges = updatedEdges.concat([secondEdge]);
            }

            // Update React Flow state
            setNodes(updatedNodes);
            setEdges(updatedEdges);
            // Update Redux state
            dispatch(setWorkflow({
                ...workflow,
                nodes: cloneNodesForRedux(updatedNodes),
                edges: cloneEdgesForRedux(updatedEdges)
            }));
            // Open details panel for specialized nodes
            if (['httpRequest', 'sharePublicForm', 'notification', 'estimation', 'duplicateCheck', 'outlookCalendar', 'googlePlaces'].includes(canonical)) {
                dispatch(setSelectedStep({ id: newNode.id, data: { type: canonical } }));
            } else {
                dispatch(setSelectedStep({ ...newNode.data, assignTo: { 'user': [], 'group': [], 'role': [] }, id: newNode.id }))

            }
            dispatch(setChangeDetected(true));
            dispatch(setInsertNodeBetween({})); // Clear the state
        }
    }, [insertNodeBetween])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (mergeTargetNode.value && selectedConnector.id) {
            let connectorNode = nodes.find(node => node.id === mergeTargetNode.sourceNode)
            let prevCurrentEdge = edges.find(edge => edge.target === connectorNode.id);

            // let updatedEdges = filterPrevEdge(edges, prevCurrentEdge) 
            let updatedEdges = edges.map(edge => {
                if (edge.id === prevCurrentEdge.id) {
                    let reqEdge = { ...prevCurrentEdge, target: mergeTargetNode.value, type: "figma", }
                    if (mergeTargetNode.type === "textInput") {
                        reqEdge.targetHandle = "targetBottom"
                    } else if (mergeTargetNode.label === "end") {
                        reqEdge.targetHandle = "targetReject"
                    }
                    return { ...reqEdge }
                } else {
                    return { ...edge }
                }
            })

            // let sourceEdge = {...prevCurrentEdge, target: mergeTargetNode.value, type: "smoothstep" }
            // updatedEdges.push(sourceEdge)

            let updatedNodes = nodes.filter(node => node.id !== connectorNode.id)
            // return
            setEdges(updatedEdges)
            setNodes(updatedNodes)
            dispatch(resetNodeType())
            dispatch(setWorkflow({ ...workflow, nodes: cloneNodesForRedux(updatedNodes), edges: cloneEdgesForRedux(updatedEdges) }))
            dispatch(setMergeTargetNode({}))
            dispatch(setChangeDetected(true))
        }

    }, [mergeTargetNode, selectedConnector])

    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge(params, eds)),
        [setEdges],
    );

    // ── Unified /ai assistant: payload builder + per-domain appliers ──

    const fetchFormConditionsForAllForms = async () => {
        const forms = Array.isArray(workflowForms) ? workflowForms : [];
        const allConditions = [];
        for (const form of forms) {
            const fid = form?.formId || form?.id;
            if (!fid) continue;
            if (formConditionsCacheRef.current[fid]) {
                allConditions.push(...formConditionsCacheRef.current[fid]);
                continue;
            }
            try {
                const res = await getFormCondition(fid);
                const data = res?.status === 200 ? (res.data || []) : [];
                formConditionsCacheRef.current[fid] = data;
                allConditions.push(...data);
            } catch (err) {
            }
        }
        return allConditions;
    };

    const buildAssistantPayload = async (prompt) => {
        // Workflow nodes (clean) + per-node conditions
        const conditionsByNodeId = new Map();
        (workflowConditions || []).forEach(cond => {
            if (!cond?.nodeId) return;
            if (!conditionsByNodeId.has(cond.nodeId)) conditionsByNodeId.set(cond.nodeId, []);
            conditionsByNodeId.get(cond.nodeId).push(cond);
        });
        const workflowNodes = (nodes || []).map(node => {
            const entry = {
                id: node.id,
                type: node.type,
                position: node.position ? { x: node.position.x, y: node.position.y } : undefined,
                data: node.data ? { ...node.data } : {},
                formData: node.formData ? { ...node.formData } : {},
            };
            delete entry.data?.verificationGroup;
            const existing = conditionsByNodeId.get(node.id);
            if (existing && existing.length > 0) entry.conditions = existing;
            return entry;
        });

        // Per-form widget index. Each form keeps its identity so the AI can
        // tell which widgets live where and which form a request targets.
        const forms = (workflowForms || []).map(form => {
            const widgets = getAllWidgets(form?.items || []);
            const seen = new Set();
            const items = [];
            widgets.forEach(w => {
                if (!w || !w.id || seen.has(w.id)) return;
                seen.add(w.id);
                const item = { id: w.id, label: w.label, formType: w.formType };
                if (w.options !== undefined) item.options = w.options;
                items.push(item);
            });
            return {
                formId: form.formId || form.id,
                formName: form.formName || form.name || form.formHeader?.label,
                nodeId: form.nodeId,
                items,
            };
        });

        const slimUsers = (spaceSpecificUsers || []).map(u => ({ name: u.name, email: u.email, userId: u.userId }));
        const slimGroups = (spaceSpecificGroups || []).map(g => ({ name: g.name, value: g.groupId }));
        const slimUserPickers = (spaceSpecificUserPickers || []).map(p => ({ id: p.id, label: p.label, value: p.value ?? p.widgetId }));
        let spaceRoles = [];
        try {
            const rolesData = await getRoles();
            spaceRoles = (Array.isArray(rolesData) ? rolesData : []).map(r => ({
                name: r.role || r.name || r.label,
                value: r.value || r.role,
            }));
        } catch (err) {
        }

        const formConditions = await fetchFormConditionsForAllForms();

        return {
            prompt,
            domain: "workflow",
            forms,
            formConditions,
            workflowNodes,
            spaceUsers: slimUsers,
            spaceGroups: slimGroups,
            spaceRoles,
            spaceUserPickers: slimUserPickers,
            formData: {},
            workflowSteps: [],
        };
    };

    const applyWorkflowFromAi = async (data, ctx) => {
        const respNodes = Array.isArray(data?.node) ? data.node : (Array.isArray(data?.nodes) ? data.nodes : []);
        const respEdges = Array.isArray(data?.edge) ? data.edge : (Array.isArray(data?.edges) ? data.edges : []);

        const keptNodes = respNodes.filter(n => !n?.delete);
        const keptEdges = respEdges.filter(e => !e?.delete);
        const deletedNodeIds = respNodes.filter(n => n?.delete).map(n => n.id);
        const deletedEdgeObjs = respEdges.filter(e => e?.delete);

        // Persist the workflow immediately so downstream appliers (form, conditions)
        // in this same batch have a real workflowId / nodeIds to attach to.
        const existing = workflowDataRef.current || workflowData || {};
        const isUpdate = !!existing.workflowId;
        const postData = isUpdate
            ? { ...existing, node: respNodes, edge: respEdges }
            : { spaceId, node: keptNodes, edge: keptEdges, deletedFormId: [] };

        showLoader(true);
        const saveResp = await saveWorkflow(postData, isUpdate);
        showLoader(false);

        if (saveResp.status === 200 && saveResp.data) {
            workflowDataRef.current = saveResp.data;
            setWorkflowData(saveResp.data);
            dispatch(setWorkflow({
                workflowId: saveResp.data.workflowId,
                nodes: saveResp.data.node || keptNodes,
                edges: saveResp.data.edge || keptEdges,
            }));
            dispatch(setOriginalWorkflow({ nodes: saveResp.data.node || [], edges: saveResp.data.edge || [] }));
            dispatch(setChangeDetected(false));
        } else {
            // Save failed — still reflect the AI changes locally so the user can retry manually.
            dispatch(setWorkflow({
                workflowId: data?.workflowId || existing.workflowId,
                nodes: keptNodes,
                edges: keptEdges,
            }));
            dispatch(setChangeDetected(true));
        }

        if (deletedNodeIds.length) {
            setDeletedFlowNodes(prev => [...prev, ...deletedNodeIds]);
            dispatch(setDeletedNodes((deletedNodes || []).concat(deletedNodeIds)));
        }
        if (deletedEdgeObjs.length) {
            setDeletedEdges(prev => [...prev, ...deletedEdgeObjs]);
        }

        if (Array.isArray(ctx?.nodeUpdates) && ctx.nodeUpdates.length > 0) {
            applyNodeUpdates(ctx.nodeUpdates);
        }

        return {
            ok: saveResp.status === 200,
            message: saveResp.status === 200
                ? (saveResp.message || "Workflow saved.")
                : (saveResp.message || "Workflow updated locally; save failed — please try again."),
        };
    };

    const applyNodeUpdates = (nodeUpdates) => {
        const updatesById = new Map();
        nodeUpdates.forEach(u => { if (u && u.nodeId) updatesById.set(u.nodeId, u); });
        const currentNodes = Array.isArray(workflow.nodes) ? workflow.nodes : [];
        const mergedNodes = currentNodes.map(node => {
            const update = updatesById.get(node.id);
            if (!update) return node;
            const { nodeId, ...rest } = update;
            return { ...node, data: { ...(node.data || {}), ...rest } };
        });
        dispatch(setWorkflow({
            workflowId: workflow.workflowId || workflowData?.workflowId,
            nodes: mergedNodes,
            edges: workflow.edges || [],
        }));
        dispatch(setChangeDetected(true));
    };

    const applyWorkflowConditionFromAi = async (data, ctx) => {
        // Read-only "list" action: render the conditions instead of mutating.
        if (String(ctx?.action).toLowerCase().trim() === "list") {
            return listConditionsMessage(data, "workflow");
        }

        const wfid = workflowDataRef.current?.workflowId || workflowData?.workflowId;
        let mutated = false;
        if (ctx?.action === "delete" && data?.conditionId) {
            await deleteWorkflowCondition(data.conditionId);
            mutated = true;
        } else if (data) {
            await saveWorkflowCondition({ ...data, workflowId: wfid });
            mutated = true;
        }
        if (Array.isArray(ctx?.nodeUpdates) && ctx.nodeUpdates.length > 0) {
            applyNodeUpdates(ctx.nodeUpdates);
        }
        if (wfid && mutated) {
            await fetchNodeConditions(wfid);
        }
        return { ok: true, skipped: !mutated };
    };

    const applyFormFromAi = async (data, ctx) => {
        // Read-only "list" action: render the forms instead of mutating.
        if (String(ctx?.action).toLowerCase().trim() === "list") {
            return listFormsMessage(data);
        }

        // Form changes triggered from the workflow page are saved immediately.
        // (The form-editor page keeps changes in preview-only mode.)
        // formId / formName / nodeId live at the result envelope level (ctx.raw),
        // not inside `data`. `data` carries items (and optionally formHeader).
        const raw = ctx?.raw || {};
        const action = ctx?.action;
        const fid = raw.formId || data?.formId || "";
        const nodeId = raw.nodeId || data?.nodeId;
        const wfid = workflowDataRef.current?.workflowId || workflowData?.workflowId;

        // Delete can be signalled by ctx.action OR a `delete: true` flag on data.
        if (action === "delete" || data?.delete === true) {
            const targetFormId = data?.formId || fid;
            if (!targetFormId) {
                return { ok: true, skipped: true };
            }
            showLoader(true);
            const deleteResp = await deleteForm(targetFormId);
            showLoader(false);
            if (deleteResp?.status === 200) {
                // Unbind the deleted form from its node, mirroring handleDeleteForm.
                if (nodeId) {
                    const currentNodes = Array.isArray(workflow.nodes) ? workflow.nodes : [];
                    const mergedNodes = currentNodes.map(node =>
                        node.id === nodeId
                            ? { ...node, formData: { ...(node.formData || {}), formId: "", formName: "" } }
                            : node
                    );
                    dispatch(setWorkflow({
                        workflowId: workflow.workflowId || workflowData?.workflowId,
                        nodes: mergedNodes,
                        edges: workflow.edges || [],
                    }));
                    dispatch(setChangeDetected(true));
                }
                delete formConditionsCacheRef.current[targetFormId];
                await fetchWorkflowForms();
                return { ok: true, message: ctx?.raw?.message };
            }
            return { ok: false, message: deleteResp?.message || "Failed to delete form." };
        }

        // Generate responses can nest items under several keys; mirror the
        // form-editor's lookup so "domain:form, action:generate" persists.
        const unwrapped = data?.data || data?.result || data || {};
        const rawItems =
            (Array.isArray(unwrapped.items) && unwrapped.items) ||
            (Array.isArray(unwrapped.formElements) && unwrapped.formElements) ||
            (Array.isArray(unwrapped.form?.items) && unwrapped.form.items) ||
            [];
        const headerFromPayload = unwrapped.formHeader || unwrapped.form?.formHeader;
        const formName = raw.formName || headerFromPayload?.label;

        const usedIds = new Set();
        const normalizeItems = (arr = []) => arr.map(item => {
            const next = { ...item };
            if (next.type && !next.formType) {
                next.formType = next.type;
                delete next.type;
            }
            if (!next.id || usedIds.has(next.id)) {
                next.id = uuidv4();
            }
            usedIds.add(next.id);
            if (Array.isArray(next.children) && next.children.length > 0) {
                next.children = normalizeItems(next.children);
            }
            return next;
        });
        const items = normalizeItems(rawItems);

        let savedFormId = fid;
        if (items.length > 0 && nodeId) {
            const existing = fid ? (workflowForms || []).find(f => (f.formId || f.id) === fid) : null;
            const payload = {
                spaceId,
                nodeId,
                items,
                formHeader: headerFromPayload || existing?.formHeader || {
                    label: formName || existing?.name || existing?.label || "Form",
                },
            };
            if (wfid) payload.workflowId = wfid;
            if (fid) payload.formId = fid;
            const saveResp = await saveNodeFormData(payload);
            if (saveResp?.status === 200 && saveResp?.data?.formId) {
                savedFormId = saveResp.data.formId;
            }
        }

        if (action === "generate" && nodeId && savedFormId) {
            const currentNodes = Array.isArray(workflow.nodes) ? workflow.nodes : [];
            const mergedNodes = currentNodes.map(node => {
                if (node.id !== nodeId) return node;
                return {
                    ...node,
                    formData: {
                        ...(node.formData || {}),
                        formId: savedFormId,
                        formName: formName || node.formData?.formName || "",
                    },
                };
            });
            dispatch(setWorkflow({
                workflowId: workflow.workflowId || workflowData?.workflowId,
                nodes: mergedNodes,
                edges: workflow.edges || [],
            }));
            dispatch(setChangeDetected(true));
        }

        await fetchWorkflowForms();
        return { ok: true };
    };

    const applyFormConditionFromAi = async (data, ctx) => {
        // Read-only "list" action: render the conditions instead of mutating.
        if (String(ctx?.action).toLowerCase().trim() === "list") {
            return listConditionsMessage(data, "form");
        }

        // The AI may return either a single condition object or an array of them
        // (e.g. bulk delete returns [{ conditionId, delete: true }, ...]).
        const entries = Array.isArray(data) ? data : data ? [data] : [];
        const envelopeFid = ctx?.raw?.formId || "";
        const touchedFids = new Set();
        let mutated = false;
        let invalidateAll = false;
        for (const entry of entries) {
            // formId may live on the entry or on the envelope (ctx.raw).
            const fid = entry?.formId || envelopeFid;
            // Delete can be signalled by ctx.action OR a `delete: true` flag on the entry.
            const wantsDelete = ctx?.action === "delete" || entry?.delete === true;
            if (wantsDelete) {
                if (entry?.conditionId) {
                    await deleteFormCondition(entry.conditionId);
                    mutated = true;
                    if (fid) touchedFids.add(fid);
                    else invalidateAll = true;
                }
            } else if (entry) {
                const payload = { ...entry };
                if (fid) payload.formId = fid;
                await saveFormCOndition(payload);
                mutated = true;
                if (fid) touchedFids.add(fid);
                else invalidateAll = true;
            }
        }
        if (mutated) {
            if (invalidateAll) {
                formConditionsCacheRef.current = {};
            } else {
                touchedFids.forEach(fid => { delete formConditionsCacheRef.current[fid]; });
            }
        }
        return { ok: true, skipped: !mutated };
    };

    const handleChatbotSummarize = () => {
        if (!Array.isArray(nodes) || nodes.length === 0) {
            return "The canvas is empty.";
        }
        const counts = {};
        let connectorCount = 0;
        nodes.forEach(n => {
            if (n?.type === "default" && n?.data?.type === "start") {
                counts["Start"] = (counts["Start"] || 0) + 1;
                return;
            }
            if (n?.type === "textInput") {
                const k = n?.data?.approvalStep ? "Approval Step" : "Workflow Step";
                counts[k] = (counts[k] || 0) + 1;
                return;
            }
            if (n?.type === "circle") {
                const t = n?.data?.type;
                if (!t) { connectorCount++; return; }
                const label = NODE_TYPE_LABEL[t] || t;
                counts[label] = (counts[label] || 0) + 1;
                return;
            }
            if (n?.type === "output") {
                const label = n?.data?.type === "approve" ? "Approved" : "Rejected";
                counts[label] = (counts[label] || 0) + 1;
                return;
            }
        });

        const lines = Object.entries(counts).map(([k, v]) => `• ${k}: ${v}`);
        const header = `Workflow summary (${nodes.length} nodes${connectorCount ? `, ${connectorCount} open +` : ""}):`;
        return lines.length ? `${header}\n${lines.join("\n")}` : "The workflow has no configured steps yet.";
    };

    const submitWorkflow = async () => {
        // Create clean node objects by explicitly copying only the properties we need
        let updatedNodes = nodes.map(node => {
            // Create a clean node object with only serializable properties
            const cleanData = node.data ? { ...node.data } : {};
            delete cleanData.verificationGroup;
            const cleanNode = {
                id: node.id,
                type: node.type,
                position: {
                    x: node.position.x,
                    y: node.position.y
                },
                data: cleanData,
                formData: node.formData ? { ...node.formData } : {}
            };

            // Add optional properties only if they exist and are not internal React Flow properties
            if (node.draggable !== undefined) cleanNode.draggable = node.draggable;
            if (node.selectable !== undefined) cleanNode.selectable = node.selectable;
            if (node.style) cleanNode.style = { ...node.style };
            if (node.className) cleanNode.className = node.className;
            if (node.targetPosition) cleanNode.targetPosition = node.targetPosition;
            if (node.sourcePosition) cleanNode.sourcePosition = node.sourcePosition;
            if (node.hidden !== undefined) cleanNode.hidden = node.hidden;
            if (node.zIndex !== undefined) cleanNode.zIndex = node.zIndex;
            if (node.extent) cleanNode.extent = node.extent;
            if (node.expandParent !== undefined) cleanNode.expandParent = node.expandParent;
            if (node.parentId) cleanNode.parentId = node.parentId;

            // Only include explicit width/height if they were set by the user (not measured by React Flow)
            if (node.width && !node.measured) cleanNode.width = node.width;
            if (node.height && !node.measured) cleanNode.height = node.height;
            if (node.initialWidth) cleanNode.initialWidth = node.initialWidth;
            if (node.initialHeight) cleanNode.initialHeight = node.initialHeight;

            return cleanNode;
        })
        let postEdges = (edges || []).filter(edge => !(edge?.data && edge.data.displayOnly)).map(edge => {
            const cleanEdge = { ...edge };
            if (cleanEdge.hidden !== undefined) delete cleanEdge.hidden;
            if (cleanEdge.data && cleanEdge.data.displayOnly) {
                const { displayOnly, ...rest } = cleanEdge.data;
                cleanEdge.data = rest;
            }
            return cleanEdge;
        })
        let postData = { spaceId: spaceId, edge: postEdges, node: updatedNodes, deletedFormId: [] }
        let updating = workflowData.workflowId ? true : false
        if (updating && workflowData?.workflowId) {
            let updatedNodes = [], updatedEdges = [];
            postData.node.forEach(nodeData => {
                let initialNodeFormat = workflowData.node.find(data => data.id === nodeData.id);
                if (initialNodeFormat) {
                    let objMatch = isEqual(initialNodeFormat, nodeData)
                    if (objMatch) {
                        return
                    }
                    updatedNodes.push({ ...nodeData, delete: false })
                } else {
                    updatedNodes.push(nodeData)
                }
            })
            if (deletedFlowNodes.length > 0) {
                deletedFlowNodes.forEach(node => {
                    let initialNode = workflowData.node.find(data => data.id === node);
                    updatedNodes.push({ ...initialNode, delete: true })
                })
            }
            postData.edge.forEach(edgeData => {
                let initialNodeFormat = workflowData.edge.find(data => data.id === edgeData.id);
                if (initialNodeFormat) {
                    let objMatch = isEqual(initialNodeFormat, edgeData)
                    if (objMatch) {
                        // edgeData["delete"] = false;
                        return
                    }
                    updatedEdges.push({ ...edgeData, delete: false })
                } else {
                    updatedEdges.push({ ...edgeData })
                }
            })
            if (deletedEdges.length > 0) {
                deletedEdges.forEach(edge => {
                    updatedEdges.push({ ...edge, delete: true })
                })
            }
            // updatedNodes = updatedNodes.map(node => {
            //     let edgeOfCurrentNode = edges.find(edge => edge.target === node.id)
            //     if (!edgeOfCurrentNode) {
            //         return { ...node, delete: true }
            //     }
            //     return { ...node }
            // })
            postData = { ...workflowData, node: updatedNodes, edge: updatedEdges }
        }

        // return
        showLoader(true)
        let saveResp = await saveWorkflow(postData, updating)
        showLoader(false)
        if (saveResp.status === 200) {
            message.success(saveResp.message)
            setWorkflowData(saveResp.data)
            dispatch(setWorkflow({ workflowId: saveResp.data.workflowId, nodes: saveResp.data.node, edges: saveResp.data.edge }))
        } else {
            message.error(saveResp.message)
        }
    }

    const handleChatbotSave = async () => {
        try {
            await submitWorkflow();
            return { ok: true, message: "Workflow saved." };
        } catch (error) {
            return { ok: false, message: error?.message || "Failed to save the workflow." };
        }
    };

    const buildWorkflowPayloadForAI = () => {
        const cleanNodes = nodes.map(node => {
            const cleanData = node.data ? { ...node.data } : {};
            delete cleanData.verificationGroup;
            return {
                id: node.id,
                type: node.type,
                position: { x: node.position?.x, y: node.position?.y },
                data: cleanData,
                formData: node.formData ? { ...node.formData } : {},
            };
        });
        const cleanEdges = (edges || [])
            .filter(edge => !(edge?.data && edge.data.displayOnly))
            .map(edge => {
                const clean = { ...edge };
                if (clean.hidden !== undefined) delete clean.hidden;
                if (clean.data && clean.data.displayOnly) {
                    const { displayOnly, ...rest } = clean.data;
                    clean.data = rest;
                }
                return clean;
            });
        return {
            workflowId: workflowData.workflowId,
            spaceId: spaceId,
            tenantId: workflowData.tenantId,
            node: cleanNodes,
            edge: cleanEdges,
        };
    };

    const handleChatbotConditions = async (prompt) => {
        const trimmedPrompt = (prompt || "").trim();
        if (!trimmedPrompt) {
            return { ok: false, message: "Please enter a prompt." };
        }
        try {
            const formItems = [];
            const seenIds = new Set();
            (workflowForms || []).forEach(form => {
                const widgets = getAllWidgets(form?.items || []);
                widgets.forEach(w => {
                    if (!w || !w.id || seenIds.has(w.id)) return;
                    seenIds.add(w.id);
                    formItems.push({ id: w.id, label: w.label, formType: w.formType });
                });
            });

            const conditionsByNodeId = new Map();
            (workflowConditions || []).forEach(cond => {
                if (!cond?.nodeId) return;
                if (!conditionsByNodeId.has(cond.nodeId)) conditionsByNodeId.set(cond.nodeId, []);
                conditionsByNodeId.get(cond.nodeId).push(cond);
            });

            const workflowNodes = (nodes || []).map(node => {
                const nodeEntry = {
                    id: node.id,
                    type: node.type,
                    data: node.data ? { ...node.data } : {},
                    formData: node.formData ? { ...node.formData } : {},
                };
                const existing = conditionsByNodeId.get(node.id);
                if (existing && existing.length > 0) {
                    nodeEntry.conditions = existing;
                }
                return nodeEntry;
            });

            const spaceUsers = (spaceSpecificUsers || []).map(user => ({
                name: user.name,
                email: user.email,
                userId: user.userId,
            }));
            const spaceGroups = (spaceSpecificGroups || []).map(group => ({
                name: group.name,
                value: group.groupId,
            }));

            const rolesData = await getRoles();
            const spaceRoles = (Array.isArray(rolesData) ? rolesData : []).map(r => ({
                name: r.role || r.name || r.label,
                value: r.value || r.role,
            }));

            const payload = {
                prompt: trimmedPrompt,
                formItems,
                workflowNodes,
                spaceUsers,
                spaceGroups,
                spaceRoles,
                conditions: Array.isArray(workflowConditions) ? workflowConditions : [],
            };

            const response = await upsertWorkflowConditionAI(payload);
            if (response.status !== 200 || !response.data) {
                return {
                    ok: false,
                    message: response.message || "The assistant couldn't generate the condition. Please try again.",
                };
            }

            const raw = response.data?.conditions || response.data;
            const generated = Array.isArray(raw) ? raw : (raw && typeof raw === "object" ? [raw] : []);
            if (generated.length === 0) {
                return { ok: false, message: response.message || "No conditions returned from generator." };
            }

            showLoader(true);
            for (const cond of generated) {
                if (cond?.delete && cond?.conditionId) {
                    await deleteWorkflowCondition(cond.conditionId);
                    continue;
                }
                const postData = { ...cond, workflowId: workflowData?.workflowId };
                await saveWorkflowCondition(postData);
            }
            showLoader(false);

            if (Array.isArray(response.nodeUpdates) && response.nodeUpdates.length > 0) {
                const updatesById = new Map();
                response.nodeUpdates.forEach(update => {
                    if (update && update.nodeId) updatesById.set(update.nodeId, update);
                });
                const currentNodes = Array.isArray(workflow.nodes) ? workflow.nodes : [];
                const mergedNodes = currentNodes.map(node => {
                    const update = updatesById.get(node.id);
                    if (!update) return node;
                    const { nodeId, ...rest } = update;
                    return { ...node, data: { ...(node.data || {}), ...rest } };
                });
                dispatch(setWorkflow({
                    workflowId: workflow.workflowId || workflowData?.workflowId,
                    nodes: mergedNodes,
                    edges: workflow.edges || [],
                }));
                dispatch(setChangeDetected(true));
            }

            if (workflowData?.workflowId) {
                await fetchNodeConditions(workflowData.workflowId);
            }

            return {
                ok: true,
                message: response.message || "Condition added to the workflow.",
            };
        } catch (error) {
            showLoader(false);
            return { ok: false, message: error?.message || "Failed to generate workflow condition." };
        }
    };

    const handleChatbotUpsert = async (prompt) => {
        try {
            const hasCanvasContent = Array.isArray(nodes) && nodes.length > 1;
            const workflowPayload = (workflowData?.workflowId || hasCanvasContent) ? buildWorkflowPayloadForAI() : undefined;
            const spaceUsers = (spaceSpecificUsers || []).map(user => ({
                name: user.name,
                email: user.email,
                userId: user.userId,
            }));
            const spaceGroups = (spaceSpecificGroups || []).map(group => ({
                name: group.name,
                value: group.groupId,
            }));
            const spaceUserPickers = (spaceSpecificUserPickers || []).map(picker => ({
                id: picker.id,
                label: picker.label,
                value: picker.value ?? picker.widgetId,
            }));
            const response = await upsertWorkflowAI(prompt, workflowPayload, {
                spaceUsers,
                spaceGroups,
                spaceUserPickers,
            });

            if (response.status !== 200 || !response.data) {
                return {
                    ok: false,
                    message: response.message || "The assistant couldn't update the workflow. Please try again.",
                };
            }

            const respNodes = Array.isArray(response.data.node) ? response.data.node : [];
            const respEdges = Array.isArray(response.data.edge) ? response.data.edge : [];

            const keptNodes = respNodes.filter(n => !n?.delete);
            const keptEdges = respEdges.filter(e => !e?.delete);
            const deletedNodeIds = respNodes.filter(n => n?.delete).map(n => n.id);
            const deletedEdgeObjs = respEdges.filter(e => e?.delete);

            dispatch(setWorkflow({
                workflowId: response.data.workflowId || workflowData?.workflowId,
                nodes: keptNodes,
                edges: keptEdges,
            }));

            if (deletedNodeIds.length) {
                setDeletedFlowNodes(prev => [...prev, ...deletedNodeIds]);
                dispatch(setDeletedNodes((deletedNodes || []).concat(deletedNodeIds)));
            }
            if (deletedEdgeObjs.length) {
                setDeletedEdges(prev => [...prev, ...deletedEdgeObjs]);
            }

            dispatch(setChangeDetected(true));

            return {
                ok: true,
                message: response.message || "Workflow updated. Review the canvas and hit Save to persist.",
            };
        } catch (error) {
            return { ok: false, message: error?.message || "Failed to generate workflow." };
        }
    };

    const onNodesDelete = (deleted) => {
        setEdges(
            deleted.reduce((acc, node) => {
                const incomers = getIncomers(node, nodes, edges);
                const outgoers = getOutgoers(node, nodes, edges);
                const connectedEdges = getConnectedEdges([node], edges);

                const remainingEdges = acc.filter(
                    (edge) => !connectedEdges.includes(edge),
                );

                const createdEdges = incomers.flatMap(({ id: source }) =>
                    outgoers.map(({ id: target }) => ({
                        id: `${source}->${target}`,
                        source,
                        target,
                    })),
                );

                return [...remainingEdges, ...createdEdges];
            }, edges),
        );
    }

    const handleNodeDelete = (nodeId) => {
        let nodeToBeDeleted = nodes.find(node => node.id === nodeId)
        onNodesDelete([nodeToBeDeleted])
    }

    const navigateToNotification = () => {
        navigate(`/app/space/${spaceId}/notification-center`)
    }
    const navigateToSpaceDashboard = () => {
        navigate("/app/space/" + spaceId + "/documents")
    }

    return (
        <div className="workflow-layout">
            <Header className="app-header workflow-header">
                <div className="workflow-header-left">
                    <BreadCrumb items={breadcrumbs} marginb="0px" />
                </div>
                <Flex className="workflow-header-actions" justify="flex-end" align="center">
                    <Button
                        className='workflow-btn subtle'
                        icon={<MailOutlined />}
                        onClick={navigateToNotification}
                    >
                        {t("Notifications")}
                    </Button>
                    <Button
                        className="workflow-btn primary"
                        onClick={submitWorkflow}
                        id="workflowSubmit"
                    >
                        {t("Save")}
                    </Button>
                </Flex>
            </Header>
            <div className="workflow-main">
                <div className="workflow-flow-wrapper" ref={flowWrapperRef}>
                    <ReactFlowProvider>
                        <FlowInner
                            nodes={nodes}
                            edges={edges}
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            handleNodesChange={handleNodesChange}
                            onNodeDragStop={handleNodeDragStop}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                        />
                    </ReactFlowProvider>

                    {/* Baseline guide (from Figma reference) */}
                    {/* <div style={{
                    position: 'absolute',
                    top: '180px',
                    left: 0,
                    width: '100%',
                    height: '1px',
                    background: 'linear-gradient(90deg, rgba(59,130,246,0.25) 0%, rgba(59,130,246,0) 100%)',
                    pointerEvents: 'none'
                }} /> */}
                    <StepDetailsForm />
                    <NotificationInfo />
                    <HttpRequestPanel />
                    <VerificationPanel />
                    <OutlookIntegrationPanel />
                    <GooglePlacesPanel />
                    <ConditionManager workflowData={workflowData} />
                    <JoinRuleForm />
                    <WorkflowSidebar
                        isOpen={sidebarOpen}
                        selectedData={sidebarData}
                        selectedType={sidebarType}
                        onClose={() => dispatch(setSidebarOpen(false))}
                    />
                </div>
            </div>
            {/* <AssistantBot
                title="AI Workflow Assistant"
                welcomeMessage='Hi! I can help build workflows, set up conditions, and edit the bound form. Type "save" to persist your changes.'
                placeholder="Describe what you'd like to build"
                apiUrl={ASSISTANT_API_URL}
                allowedDomains={SCOPE.WORKFLOW_BUILDER}
                handlers={{
                    workflow:          (data, ctx) => applyWorkflowFromAi(data, ctx),
                    workflowCondition: (data, ctx) => applyWorkflowConditionFromAi(data, ctx),
                    form:              (data, ctx) => applyFormFromAi(data, ctx),
                    formCondition:     (data, ctx) => applyFormConditionFromAi(data, ctx),
                }}
                buildPayload={buildAssistantPayload}
                extraCommands={[
                    {
                        match: (t) => /\b(save( workflow| it)?|submit( workflow)?)\b/i.test(t),
                        run:   async () => handleChatbotSave(),
                    },
                    {
                        match: (t) => /\b(summari[sz]e|summary|describe workflow|list (nodes|steps)|show (nodes|steps)|what do i have|overview)\b/i.test(t),
                        run:   async () => ({ ok: true, message: handleChatbotSummarize() }),
                    },
                ]}
            /> */}
        </div>
    )
}

export default Canvas

function FlowInner({
    nodes,
    edges,
    nodeTypes,
    edgeTypes,
    handleNodesChange,
    onNodeDragStop,
    onEdgesChange,
    onConnect,
}) {
    return (
        <div style={{ width: '100%', height: "90%" }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={handleNodesChange}
                onNodeDragStop={onNodeDragStop}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                attributionPosition="top-right"
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                panOnDrag={true}
                panOnScroll={true}
                panOnScrollMode="free"
                zoomOnScroll={true}
                zoomOnPinch={true}
                zoomOnDoubleClick={true}
                preventScrolling={true}
                selectNodesOnDrag={true}
                nodesDraggable={true}
                multiSelectionKeyCode={null}
                selectionKeyCode={null}
                proOptions={{ hideAttribution: true }}
                style={{
                    background: `url(${canvasBackground})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            >
                <Controls />
            </ReactFlow>
        </div>
    )
}
